/**
 * Disbursement Files API Module
 *
 * TODO: Backend Integration Required
 * This file is a placeholder for future API integration.
 * Once the backend endpoints are ready, implement the functions below.
 *
 * Required Backend Endpoint:
 * GET /disbursements
 *   - Query params: page, limit, sortBy, sortOrder, status, channel
 *   - Returns: { data: DisbursementFile[], pagination: PaginationData }
 *
 * GET /disbursements/:id
 *   - Returns: DisbursementFile with full relationship details
 *
 * GET /payment-files/:id/disbursements
 *   - Returns: All disbursement files related to a specific payment file
 */

import { config } from '@/config';
import type { DisbursementFile } from '@/types/disbursement';
import keycloakApi, { getAuthToken } from './keycloak-auth';

export const FILE_SERVER_UNREACHABLE_MESSAGE =
  'Service temporarily unavailable — try again shortly';
export const NETWORK_ERROR_MESSAGE = 'Unable to reach server';

export interface DisbursementFileFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  status?: string | string[];
  channel?: string | string[];
  paymentFileId?: string; // Filter by related payment file
}

export interface DisbursementFilesPaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface DisbursementFilesResponse {
  data: DisbursementFile[];
  pagination: DisbursementFilesPaginationData;
}

// API helper function with Keycloak bearer token
const apiCall = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  return keycloakApi.authenticatedApiCall<T>(endpoint, options);
};

/**
 * Get disbursement files with optional filtering, sorting, and pagination
 *
 * @example
 * const response = await getDisbursementFiles({
 *   page: 1,
 *   limit: 10,
 *   sortBy: 'created_at',
 *   order: 'desc',
 *   status: ['processing', 'completed'],
 *   channel: 'ach'
 * });
 */
export const getDisbursementFiles = async (
  filters: DisbursementFileFilters = {}
): Promise<DisbursementFilesResponse> => {
  const searchParams = new URLSearchParams();

  // Add filters to search params
  if (filters.page) searchParams.append('page', filters.page.toString());
  if (filters.limit) searchParams.append('limit', filters.limit.toString());
  if (filters.sortBy) searchParams.append('sortBy', filters.sortBy);
  if (filters.order) searchParams.append('sortOrder', filters.order.toUpperCase());

  // Handle status filter - convert array to comma-separated string
  if (filters.status) {
    const statusValue = Array.isArray(filters.status) ? filters.status.join(',') : filters.status;
    searchParams.append('status', statusValue);
  }

  // Handle channel filter
  if (filters.channel) {
    const channelValue = Array.isArray(filters.channel)
      ? filters.channel.join(',')
      : filters.channel;
    searchParams.append('channel', channelValue);
  }

  // Handle payment file filter
  if (filters.paymentFileId) {
    searchParams.append('paymentFileId', filters.paymentFileId);
  }

  const queryString = searchParams.toString();
  const endpoint = `/disbursements${queryString ? `?${queryString}` : ''}`;

  return apiCall<DisbursementFilesResponse>(endpoint);
};

/**
 * Get all disbursement files without filtering
 * This is a convenience wrapper around getDisbursementFiles
 *
 * TODO: Remove this once proper API pagination is implemented
 */
export const getAllDisbursementFiles = async (): Promise<DisbursementFilesResponse> => {
  return getDisbursementFiles({ limit: 100 });
};

/**
 * Creates one or more disbursement files from a payment file
 *
 * @param paymentFileId - The payment file ID to create disbursement for
 * @returns Disbursement creation result
 */
function extractErrorMessage(body: unknown, status: number): string {
  const errorBody: { message?: unknown; error?: unknown } =
    typeof body === 'object' && body !== null
      ? (body as { message?: unknown; error?: unknown })
      : {};

  const message = [errorBody.message, errorBody.error].find(
    (value): value is string => typeof value === 'string' && value.trim().length > 0
  );

  return message?.trim() ?? `HTTP ${status}`;
}

export async function createDisbursements(paymentFileId: string): Promise<{
  success: boolean;
  message: string;
  data?: {
    results: Array<{
      fileId: string;
      fileName: string;
      mimeType: string;
      content: string;
    }>;
  };
}> {
  const token = await getAuthToken();

  const headers: HeadersInit = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  };

  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }

  const url = `${config.api.baseURL}/disbursements`;

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({ paymentFileId }),
    headers,
    credentials: 'include',
    cache: 'no-store',
  });

  const body = await response.json();

  if (!response.ok) {
    throw new Error(extractErrorMessage(body, response.status));
  }

  if (body && typeof body === 'object' && body !== null && 'success' in body) {
    const typed = body as {
      success: boolean;
      message?: string;
      data?: {
        results: Array<{
          fileId: string;
          fileName: string;
          mimeType: string;
          content: string;
        }>;
      };
    };

    return {
      success: Boolean(typed.success),
      message: typed.message ?? 'Disbursements created successfully',
      ...(typed.data ? { data: typed.data } : {}),
    };
  }

  const dataFromBody =
    body && typeof body === 'object' && body !== null && 'data' in body
      ? ((body as { data?: unknown }).data as {
          results: Array<{
            fileId: string;
            fileName: string;
            mimeType: string;
            content: string;
          }>;
        })
      : undefined;

  return {
    success: true,
    message: 'Disbursements created successfully',
    ...(dataFromBody ? { data: dataFromBody } : {}),
  };
}

/**
 * Fetch a single disbursement, including `lastReleaseError` when the most recent
 * release attempt failed.
 */
export const getDisbursementById = async (id: string): Promise<DisbursementFile> => {
  return apiCall<DisbursementFile>(`/disbursements/${id}`);
};

export interface ReleaseDisbursementSuccess {
  success: true;
  message: string;
  data?: {
    disbursementId: string;
    status: string;
    remotePath?: string;
    timestamp?: string;
    fileSize?: number;
  };
}

/**
 * Thrown by `releaseDisbursement` so callers can branch on the failure shape
 * (structured backend body, 502 unreachable, or network rejection) without
 * re-parsing the response.
 */
export class ReleaseDisbursementError extends Error {
  status?: number;
  errorCode?: string;
  traceId?: string;
  isUnreachable: boolean;
  isNetworkError: boolean;

  constructor(
    message: string,
    opts: {
      status?: number;
      errorCode?: string;
      traceId?: string;
      isUnreachable?: boolean;
      isNetworkError?: boolean;
    } = {}
  ) {
    super(message);
    this.name = 'ReleaseDisbursementError';
    if (opts.status !== undefined) this.status = opts.status;
    if (opts.errorCode !== undefined) this.errorCode = opts.errorCode;
    if (opts.traceId !== undefined) this.traceId = opts.traceId;
    this.isUnreachable = opts.isUnreachable ?? false;
    this.isNetworkError = opts.isNetworkError ?? false;
  }
}

interface StructuredErrorBody {
  success?: boolean;
  message?: string;
  errorCode?: string;
  traceId?: string;
  // Older 422 envelope uses `error` instead of `errorCode`; surface it as the message fallback.
  error?: string;
}

/**
 * Release a disbursement (sends the file via SFTP and marks it Released).
 *
 * Bypasses the shared `apiCall` helper so we can surface structured failure
 * fields (`errorCode`, `traceId`) and distinguish 502 FILE_SERVER_UNREACHABLE
 * + fetch rejections from generic 4xx/5xx responses.
 */
export const releaseDisbursement = async (
  disbursementId: string
): Promise<ReleaseDisbursementSuccess> => {
  const url = `${config.api.baseURL}/disbursements/${disbursementId}/release`;
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      cache: 'no-store',
    });
  } catch {
    throw new ReleaseDisbursementError(NETWORK_ERROR_MESSAGE, { isNetworkError: true });
  }

  const body = (await response.json().catch(() => ({}))) as StructuredErrorBody &
    Partial<ReleaseDisbursementSuccess>;

  if (!response.ok) {
    const errorCode = body.errorCode;
    const isUnreachable = response.status === 502 && errorCode === 'FILE_SERVER_UNREACHABLE';
    const message = isUnreachable
      ? FILE_SERVER_UNREACHABLE_MESSAGE
      : body.message || body.error || `HTTP ${response.status}`;
    throw new ReleaseDisbursementError(message, {
      status: response.status,
      ...(errorCode !== undefined && { errorCode }),
      ...(body.traceId !== undefined && { traceId: body.traceId }),
      isUnreachable,
    });
  }

  return {
    success: true,
    message: body.message ?? 'Disbursement released successfully',
    ...(body.data !== undefined && { data: body.data }),
  };
};
